import type { EventBus } from '../../services/EventBus';
import type { GlobalBestState } from './models/global-best';
import type { LeaderboardRepository } from './services/leaderboard-service';

export class GlobalBestController {
  private state: GlobalBestState = { status: 'loading', value: null };
  private requestVersion = 0;
  private destroyed = false;
  private readonly disposers: Array<() => void>;

  public constructor(
    private readonly events: EventBus,
    private readonly repository: LeaderboardRepository,
  ) {
    this.disposers = [
      events.on('leaderboard:updated', () => void this.refresh()),
      events.on('leaderboard:score-saved', ({ score }) => this.handleSavedScore(score)),
    ];
  }

  public start(): void {
    void this.refresh();
  }

  public destroy(): void {
    this.destroyed = true;
    this.requestVersion += 1;
    this.disposers.forEach((dispose) => dispose());
  }

  private async refresh(): Promise<void> {
    const requestVersion = ++this.requestVersion;
    this.publish({ status: 'loading', value: null });
    try {
      const value = await this.repository.getGlobalBest();
      if (this.destroyed || requestVersion !== this.requestVersion) return;
      this.publish(value === null ? { status: 'empty', value: null } : { status: 'loaded', value });
    } catch {
      if (this.destroyed || requestVersion !== this.requestVersion) return;
      this.publish({ status: 'error', value: null });
    }
  }

  private handleSavedScore(score: number): void {
    if (this.state.status === 'loaded' && score <= this.state.value) return;
    if (this.state.status === 'loaded' || this.state.status === 'empty') {
      this.publish({ status: 'loaded', value: score });
      return;
    }
    void this.refresh();
  }

  private publish(state: GlobalBestState): void {
    this.state = state;
    this.events.emit('leaderboard:global-best-changed', state);
  }
}
